import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Zap } from "lucide-react"
import { toast } from "sonner"
import { AvatarDropdown } from "@/components/activation/AvatarDropdown"
import { SupportDialog } from "@/components/shared/SupportDialog"
import { StatusPill } from "@/components/dashboard/StatusPill"
import { effectiveActivationStep } from "@/lib/activation"
import { useActivation } from "@/lib/activation-context"
import { useSession } from "@/lib/session-context"
import { cn } from "@/lib/utils"

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", path: "/dashboard" },
  { id: "deal-pipeline", label: "Deal Pipeline", path: "/deal-pipeline" },
  { id: "capital-pipeline", label: "Capital Pipeline", path: "/capital-pipeline" },
]

export function AppHeader({ active, status = "standby", planName, showNav = false }) {
  const { currentUser, signOut } = useSession()
  const effectivePlanName = planName ?? currentUser?.subscription?.tier_name ?? "No plan"
  const { currentStep, step2, complete } = useActivation()
  const navigate = useNavigate()
  const [supportOpen, setSupportOpen] = useState(false)
  const [supportSession, setSupportSession] = useState(0)
  const activationStep = effectiveActivationStep(currentStep, step2, complete)
  const activationComplete = Boolean(complete)
  const effectiveStatus = activationComplete ? status : "standby"
  const effectiveShowNav = showNav && activationComplete
  const brandTarget = activationComplete ? "/dashboard" : `/activation/step/${activationStep}`

  const handleNavigate = async (path) => {
    if (path === "logout") {
      try {
        await signOut()
      } catch {
        toast.error("We couldn't log you out. Please try again.")
      }
      return
    }
    if (path === "settings") return navigate("/settings")
    if (path === "billing") return navigate("/billing")
    if (path === "ai-usage") return navigate("/ai-usage")
    if (path === "contact") {
      setSupportSession((session) => session + 1)
      setSupportOpen(true)
    }
  }

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/80 shadow-sm backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-6">
        <div className="flex items-center gap-8">
          <Link to={brandTarget} className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary/30 bg-primary/20">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <h1 className="text-lg font-bold tracking-tight text-foreground">Sentinel OS</h1>
          </Link>
          {effectiveShowNav ? (
            <nav className="hidden items-center gap-6 md:flex">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={cn(
                    "border-b-2 pb-1 text-sm font-medium transition-colors",
                    active === item.id
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          ) : null}
        </div>
        <div className="flex items-center gap-4">
          <StatusPill state={effectiveStatus} />
          <AvatarDropdown
            userName={`${currentUser.first_name} ${currentUser.last_name}`}
            userEmail={currentUser.email}
            planName={effectivePlanName}
            onNavigate={handleNavigate}
          />
        </div>
      </div>
      <SupportDialog key={supportSession} open={supportOpen} onOpenChange={setSupportOpen} />
    </header>
  )
}
